import { TrashIcon } from "@/assets/icons";
import Dropdown from "@/components/ui/dropdown";
import type { IOptionItem } from "@/components/ui/dropdown/types";
import { DATA_SETTINGS_MESSAGE } from "@/constants/message";
import { TimeseriesFormat } from "@/types/dataset";

import { TIMESERIES_FORMAT_OPTIONS } from "./timeSeriesConfig";

type TimeAttributeRowProps = {
  index: number;
  attributeOptions: IOptionItem[];
  attributeId?: string;
  format?: TimeseriesFormat;
  errorMessage?: string;
  /** The row's values just passed the check. */
  isValid: boolean;
  /** A new configuration keeps its first row, so there is one to fill in. */
  isRemoveDisabled: boolean;
  isDisabled: boolean;
  onChangeAttribute: (attributeId: string) => void;
  onChangeFormat: (format: TimeseriesFormat) => void;
  onRemove: () => void;
};

const TimeAttributeRow = ({
  index,
  attributeOptions,
  attributeId,
  format,
  errorMessage,
  isValid,
  isRemoveDisabled,
  isDisabled,
  onChangeAttribute,
  onChangeFormat,
  onRemove
}: TimeAttributeRowProps) => {
  const testId = `dataset-management-time-series-row-${index}`;

  return (
    <div className="flex flex-col gap-1" data-testid={testId}>
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <Dropdown
            placeholder={DATA_SETTINGS_MESSAGE.TIMESERIES_ATTRIBUTE_PLACEHOLDER}
            data={attributeOptions}
            value={
              attributeOptions.find((option) => option.value === attributeId) ??
              null
            }
            disabled={isDisabled}
            isValidationError={Boolean(errorMessage)}
            onSelect={(option) => option && onChangeAttribute(option.value)}
          />
        </div>
        <div className="flex-1">
          <Dropdown
            placeholder={DATA_SETTINGS_MESSAGE.TIMESERIES_FORMAT_PLACEHOLDER}
            data={TIMESERIES_FORMAT_OPTIONS}
            value={
              TIMESERIES_FORMAT_OPTIONS.find(
                (option) => option.value === format
              ) ?? null
            }
            disabled={isDisabled}
            onSelect={(option) =>
              option && onChangeFormat(option.value as TimeseriesFormat)
            }
          />
        </div>
        <button
          type="button"
          className="bg-transparent border-0 p-0 disabled:opacity-40 disabled:cursor-not-allowed"
          disabled={isRemoveDisabled || isDisabled}
          onClick={onRemove}
          aria-label={`Remove time attribute ${index + 1}`}
          data-testid={`${testId}-remove-button`}
        >
          <TrashIcon
            className={
              isRemoveDisabled
                ? "w-5 h-5 fill-dim-gray"
                : "w-5 h-5 fill-cinnabar"
            }
          />
        </button>
      </div>
      {isValid && !errorMessage && (
        <output
          className="text-[10px] text-green-600"
          data-testid={`${testId}-success`}
        >
          {DATA_SETTINGS_MESSAGE.TIMESERIES_VALID}
        </output>
      )}
      {errorMessage && (
        <span
          role="alert"
          className="text-[10px] text-cinnabar"
          data-testid={`${testId}-error`}
        >
          {errorMessage}
        </span>
      )}
    </div>
  );
};

export default TimeAttributeRow;
